import type { CategoryDto } from '@lista/shared-validation';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';
import type { SearchParams } from '../services';

/** Categories are reference data: cached long, localized by the current language. */
export function useCategories() {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.categories, locale],
    queryFn: services.catalog.categories,
    staleTime: 10 * 60_000,
  });
}

/** id → category plus display order, for grouping list lines. */
export function useCategoryLookup() {
  const categories = useCategories();
  const byId = new Map<string, CategoryDto & { order: number }>();
  categories.data?.forEach((c, order) => byId.set(c.id, { ...c, order }));
  return {
    byId,
    isPending: categories.isPending,
    order: (id: string) => byId.get(id)?.order ?? 999,
  };
}

/** Items of one category, page by page. */
export function useCategoryItems(categoryId: string) {
  const { services, locale } = useCore();
  return useInfiniteQuery({
    queryKey: [...queryKeys.browse(categoryId), locale],
    queryFn: ({ pageParam }) => services.catalog.browse({ categoryId }, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.page.nextCursor ?? undefined,
  });
}

/** Recently used products (BR-REC); refreshed whenever the user adds an item to a list. */
export function useRecentItems(enabled = true, limit = 20) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.recent, locale, limit],
    queryFn: () => services.catalog.recent(limit),
    enabled,
  });
}

/** Unified search; disabled below two characters (BR-SRC). Debounce the query in the UI. */
export function useItemSearch(params: SearchParams) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: queryKeys.search({ ...params, lang: locale }),
    queryFn: ({ signal }) => services.catalog.search(params, signal),
    enabled: params.q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}
